/**
 * Ephemeral per-element animation overrides, applied at render time by the
 * static scene renderer. Elements are never mutated — this module is the
 * single source of truth for "what is currently animated and by how much".
 */
import type { GradientDelta } from "./gradient";
import type { TextEffectConfig } from "./textEffects";
export type AnimatedValues = {
  /** translation delta in scene units */
  dx?: number;
  dy?: number;
  /** extra rotation around the element center, radians */
  angleDelta?: number;
  /** scale around the element center */
  scaleX?: number;
  scaleY?: number;
  /** shear around the element center, radians */
  skewX?: number;
  skewY?: number;
  /** 0..1 multiplier over the element's own opacity */
  opacity?: number;
  /** >0 lets an element that is `hidden` in the scene render while this
   * animation drives it. Baked flipbook pages use it: only page 1 is visible
   * at rest, and the rest are revealed for the duration of playback/export.
   * Layer-hiding stays absolute for everything else — nothing renders a
   * hidden element unless its own animation asks for it. */
  revealHidden?: number;
  /** canvas filter channels — neutral when undefined */
  hueRotate?: number; // degrees
  brightness?: number; // 1 = neutral
  saturate?: number; // 1 = neutral
  grayscale?: number; // 0 = neutral, 0..1
  invert?: number; // 0 = neutral, 0..1
  contrast?: number; // 1 = neutral
  sepia?: number; // 0 = neutral, 0..1
  blur?: number; // px in scene units, 0 = neutral
  /** offsets the roughjs seed — floor()ed by the renderer, so a linear ramp
   * steps through discrete redraws (boil / sketchy jitter). 0 = neutral */
  seedShift?: number;
  /** added to element.roughness before rough regenerates (sketch-in settle,
   * boil strength). 0 = neutral */
  roughnessDelta?: number;
  /** transform origin, normalized 0..1 over the element bbox — 0.5/0.5
   * (center) when undefined. Drives rotate/scale/skew pivot (swing about the
   * top edge, hinge on a corner). */
  anchorX?: number;
  anchorY?: number;
  /** fragment effect (explode/assemble): 0 intact → 1 scattered */
  fragProgress?: number;
  fragCount?: number;
  fragSpread?: number;
  fragGravity?: number;
  fragTwist?: number;
  /** 1 = shard mode (triangular halves) */
  fragShards?: number;
  /** text on path: slide position along the baked outline */
  textPathProgress?: number;
  /** flat [x,y,…] polyline baked by the textOnPath preset */
  textPathOutline?: number[];
  /** counter/odometer on text elements: interpolated displayed number */
  counterProgress?: number;
  counterFrom?: number;
  counterTo?: number;
  counterDecimals?: number;
  /** free particle emitter (confetti/sparkle) lifecycle */
  emitProgress?: number;
  emitKind?: number;
  emitCount?: number;
  emitSpread?: number;
  emitGravity?: number;
  emitSize?: number;
  /** wipe reveal: fraction of the element shown, 0..1 (1/undefined = full) */
  revealProgress?: number;
  /** wipe direction: 0 = from left, 1 = from right, 2 = from top,
   * 3 = from bottom, 4 = iris (circle growing from center) */
  revealDir?: number;
  /** marching-dash offset along strokes, px (flow along arrows/lines) */
  dashOffset?: number;
  /** dash pattern for dash flow, px (defaults 8 / 8+strokeWidth) */
  dashLen?: number;
  dashGap?: number;
  /** 0..1 stroke draw-on progress (dash-trick line drawing) */
  drawProgress?: number;
  /** 0..1 draw-window start (undefined = 0) — with drawProgress as the end,
   * a [start, end] stroke segment is shown (anime.js draw '0.25 0.75') */
  drawStart?: number;
  /** animated stroke color (CSS color string, tweened by GSAP) */
  strokeColor?: string;
  /** animated fill color (CSS color string, tweened by GSAP) */
  backgroundColor?: string;
  /** gradient stroke: added to element.strokeGradient.angle, degrees */
  gradientAngleDelta?: number;
  /** gradient stroke: added to every stop's offset (mod 1) — marching flow */
  gradientOffsetShift?: number;
  /** gradient stroke: per-stop color override, index-aligned to
   * element.strokeGradient.stops; missing indices keep their authored color */
  gradientStopColors?: string[];
  /** shape-morph target (see MORPH_SHAPES) — set statically */
  morphShape?: number;
  /** 0..1 morph progress toward morphShape */
  morphProgress?: number;
  /** flattened [x0,y0,…] normalized outline of a morph-to-element target —
   * set statically; wins over morphShape when present */
  morphOutline?: number[];
  /** 0..1 phase of flow particles traveling along a linear element */
  flowPhase?: number;
  /** number of flow particles — set statically */
  flowCount?: number;
  /** flow particle radius multiplier (× strokeWidth) — set statically */
  flowSize?: number;
  /** flow particle color (CSS color) — set statically; falls back to the
   * animated stroke color, then the element's stroke color */
  flowColor?: string;
  /** per-character text effect id (see TEXT_EFFECTS) — set statically */
  textEffect?: number;
  /** 0..1, tweened — drives the per-char effect */
  textProgress?: number;
  /** 0..1 — how much of the duration is staggered across characters */
  textStagger?: number;
  /** animation unit for text effects: 0 chars, 1 words, 2 lines */
  textSplit?: number;
  /** effect strength multiplier, 1 = default */
  textIntensity?: number;
};

/** Builds a canvas `filter` string from the animated filter channels, or null
 * when every channel is neutral. */
export const getAnimationFilter = (anim: AnimatedValues): string | null => {
  let filter = "";
  if (anim.hueRotate) {
    filter += ` hue-rotate(${anim.hueRotate}deg)`;
  }
  if (anim.brightness !== undefined && anim.brightness !== 1) {
    filter += ` brightness(${Math.max(0, anim.brightness)})`;
  }
  if (anim.saturate !== undefined && anim.saturate !== 1) {
    filter += ` saturate(${Math.max(0, anim.saturate)})`;
  }
  if (anim.grayscale) {
    filter += ` grayscale(${Math.min(1, Math.max(0, anim.grayscale))})`;
  }
  if (anim.invert) {
    filter += ` invert(${Math.min(1, Math.max(0, anim.invert))})`;
  }
  if (anim.contrast !== undefined && anim.contrast !== 1) {
    filter += ` contrast(${Math.max(0, anim.contrast)})`;
  }
  if (anim.sepia) {
    filter += ` sepia(${Math.min(1, Math.max(0, anim.sepia))})`;
  }
  if (anim.blur) {
    filter += ` blur(${Math.max(0, anim.blur)}px)`;
  }
  return filter ? filter.trim() : null;
};

let overrides: Map<string, AnimatedValues> | null = null;
let currentOwner: Map<string, AnimatedValues> | null = null;

export const setAnimationOverrides = (
  map: Map<string, AnimatedValues> | null,
) => {
  // only allow ownership transfer if no owner or same owner; prevents concurrent
  // runtimes (preview + export) from colliding and causing visual glitches
  if (currentOwner !== null && currentOwner !== map) {
    console.warn(
      "[animation] concurrent runtime conflict detected — overrides map replaced mid-playback",
    );
  }
  overrides = map;
  currentOwner = map;
};

/** Clears the global overrides only if `owner` is the currently registered
 * map — lets a runtime release its own registration without clobbering
 * another runtime that registered after it. */
export const clearAnimationOverrides = (owner: Map<string, AnimatedValues>) => {
  if (overrides === owner) {
    overrides = null;
    currentOwner = null;
  }
};

export const getAnimationOverride = (
  id: string,
): AnimatedValues | undefined => {
  return overrides?.get(id);
};

/** Runs a synchronous render with ALL overrides detached (ownership intact),
 * for renders that must show true un-animated content — e.g. thumbnails. */
export const withAnimationOverridesSuspended = <T>(fn: () => T): T => {
  const prev = overrides;
  overrides = null;
  try {
    return fn();
  } finally {
    overrides = prev;
  }
};

export type StrokeAnimation = {
  dashOffset?: number;
  dashLen?: number;
  dashGap?: number;
  drawProgress?: number;
  drawStart?: number;
};

/** Active stroke animation (dash flow / draw-on) for an element, or null. */
export const getAnimationStroke = (
  elementId: string,
): StrokeAnimation | null => {
  const override = overrides?.get(elementId);
  if (
    !override ||
    (override.dashOffset === undefined && override.drawProgress === undefined)
  ) {
    return null;
  }
  return {
    dashOffset: override.dashOffset,
    dashLen: override.dashLen,
    dashGap: override.dashGap,
    drawProgress: override.drawProgress,
    drawStart: override.drawStart,
  };
};

/** The context-transform pivot for an element's animation: the anchor
 * channels over the element bbox in scroll space, defaulting to the center
 * (exactly the legacy behavior when no anchor is set). */
export const animationPivot = (
  element: { x: number; y: number; width: number; height: number },
  anim: AnimatedValues,
  appState: { scrollX: number; scrollY: number },
): [number, number] => [
  element.x + element.width * (anim.anchorX ?? 0.5) + appState.scrollX,
  element.y + element.height * (anim.anchorY ?? 0.5) + appState.scrollY,
];

/** Active fragment effect for an element, or null when resting (progress
 * ≤ 0.001 → the normal render path, byte-identical). */
export const getAnimationFragment = (
  elementId: string,
): import("./fragments").FragmentConfig | null => {
  const override = overrides?.get(elementId);
  if (
    !override ||
    override.fragProgress === undefined ||
    override.fragProgress <= 0.001
  ) {
    return null;
  }
  return {
    progress: override.fragProgress,
    count: override.fragCount ?? 6,
    spread: override.fragSpread ?? 1,
    gravity: override.fragGravity ?? 1,
    twist: override.fragTwist ?? 1,
    shards: (override.fragShards ?? 0) >= 0.5,
  };
};

export type TextPathAnimation = {
  progress: number;
  /** flat [x,y,…] polyline */
  outline: number[];
};

/** Active text-on-path animation for a text element, or null. */
export const getAnimationTextPath = (
  elementId: string,
): TextPathAnimation | null => {
  const override = overrides?.get(elementId);
  if (
    !override ||
    override.textPathProgress === undefined ||
    !Array.isArray(override.textPathOutline) ||
    override.textPathOutline.length < 4
  ) {
    return null;
  }
  return {
    progress: override.textPathProgress,
    outline: override.textPathOutline,
  };
};

export type CounterAnimation = {
  progress: number;
  from: number;
  to: number;
  decimals: number;
};

/** Active counter/odometer animation for a text element, or null. */
export const getAnimationCounter = (
  elementId: string,
): CounterAnimation | null => {
  const override = overrides?.get(elementId);
  if (!override || override.counterProgress === undefined) {
    return null;
  }
  return {
    progress: override.counterProgress,
    from: override.counterFrom ?? 0,
    to: override.counterTo ?? 100,
    decimals: Math.max(
      0,
      Math.min(4, Math.round(override.counterDecimals ?? 0)),
    ),
  };
};

/** The displayed string for a counter at its current progress. */
export const formatCounterValue = (counter: CounterAnimation): string =>
  (
    counter.from +
    (counter.to - counter.from) * Math.max(0, Math.min(1, counter.progress))
  ).toFixed(counter.decimals);

/** Active particle emitter for an element, or null. */
export const getAnimationEmitter = (
  elementId: string,
): import("./particles").EmitterConfig | null => {
  const override = overrides?.get(elementId);
  if (!override || override.emitProgress === undefined) {
    return null;
  }
  return {
    progress: override.emitProgress,
    kind: override.emitKind ?? 0,
    count: override.emitCount ?? 24,
    spread: override.emitSpread ?? 1,
    gravity: override.emitGravity ?? 1,
    size: override.emitSize ?? 1,
  };
};

export type RoughAnimation = {
  /** integer seed offset (already floored) */
  seedShift: number;
  roughnessDelta: number;
};

/** Active rough-regeneration animation (boil / sketch-in) for an element, or
 * null when both channels are neutral. seedShift is floored here so a linear
 * tween steps through discrete redraws. */
export const getAnimationRough = (elementId: string): RoughAnimation | null => {
  const override = overrides?.get(elementId);
  if (!override) {
    return null;
  }
  const seedShift = Math.floor(override.seedShift ?? 0);
  const roughnessDelta = override.roughnessDelta ?? 0;
  if (seedShift === 0 && roughnessDelta === 0) {
    return null;
  }
  return { seedShift, roughnessDelta };
};

export type ColorAnimation = {
  stroke?: string;
  background?: string;
};

/** Active color animation for an element, or null. */
export const getAnimationColors = (
  elementId: string,
): ColorAnimation | null => {
  const override = overrides?.get(elementId);
  if (
    !override ||
    (override.strokeColor === undefined &&
      override.backgroundColor === undefined)
  ) {
    return null;
  }
  return {
    stroke: override.strokeColor,
    background: override.backgroundColor,
  };
};

/** Active gradient-stroke animation delta for an element, or null. Combine
 * with the element's static `strokeGradient` via `applyGradientDelta`. */
export const getAnimationGradientDelta = (elementId: string): GradientDelta => {
  const override = overrides?.get(elementId);
  // a fully neutral delta must be null: the channels are seeded 0 on EVERY
  // animated element, and a non-null delta here costs an allocation plus a
  // gradient rebuild per frame for animations that never touch the gradient
  if (
    !override ||
    (!override.gradientAngleDelta &&
      !override.gradientOffsetShift &&
      override.gradientStopColors === undefined)
  ) {
    return null;
  }
  return {
    angleDelta: override.gradientAngleDelta,
    offsetShift: override.gradientOffsetShift,
    stopColors: override.gradientStopColors,
  };
};

export type MorphAnimation = {
  shape: number;
  progress: number;
  /** normalized flattened target outline — wins over `shape` when present */
  outline?: number[];
};

/** Active shape morph for an element, or null. */
export const getAnimationMorph = (elementId: string): MorphAnimation | null => {
  const override = overrides?.get(elementId);
  if (
    !override ||
    override.morphShape === undefined ||
    override.morphProgress === undefined
  ) {
    return null;
  }
  return {
    shape: override.morphShape,
    progress: override.morphProgress,
    outline: override.morphOutline,
  };
};

export type FlowAnimation = {
  phase: number;
  count: number;
  size: number;
  color?: string;
};

/** Active flow-particle animation (ByteByteGo-style traveling dots) for an
 * element, or null. */
export const getAnimationFlow = (elementId: string): FlowAnimation | null => {
  const override = overrides?.get(elementId);
  if (!override || override.flowPhase === undefined) {
    return null;
  }
  return {
    phase: override.flowPhase,
    count: override.flowCount ?? 3,
    size: override.flowSize ?? 2,
    color: override.flowColor,
  };
};

/** Active per-character text effect for an element, or null. Consumed by
 * the text renderer (renderElement) to draw each glyph with its own
 * transform/alpha derived from the tweened `textProgress`. */
export const getAnimationTextEffect = (
  elementId: string,
): TextEffectConfig | null => {
  const override = overrides?.get(elementId);
  if (
    !override ||
    override.textEffect === undefined ||
    override.textProgress === undefined
  ) {
    return null;
  }
  return {
    effect: override.textEffect,
    progress: override.textProgress,
    stagger: override.textStagger ?? 0.6,
    intensity: override.textIntensity ?? 1,
    split: override.textSplit ?? 0,
  };
};

/**
 * Opacity multiplier for an element. Bound text (has `containerId`) inherits
 * its container's animated opacity so labels fade with their shape.
 */
export const getAnimationOpacity = (
  element: { id: string } & Record<string, unknown>,
): number => {
  if (!overrides) {
    return 1;
  }
  const override =
    overrides.get(element.id) ??
    (typeof element.containerId === "string"
      ? overrides.get(element.containerId)
      : undefined);
  return override?.opacity ?? 1;
};
